import React, { useEffect, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Checkbox,
    FormControlLabel,
    Slider,
    Tooltip,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import axios from 'axios';
import FolderIcon from '@mui/icons-material/Folder';
import DescriptionIcon from '@mui/icons-material/Description';
import StickyNote2OutlinedIcon from '@mui/icons-material/StickyNote2Outlined';
import { Category } from '../../types/Category';
import {
    suggestTreeFromCategory,
    suggestParentStructureOverCategories,
    fetchCategoryWithChildren,
    SUGGEST_TREE_REQUEST_TIMEOUT_MS,
} from '../../services/CategoryService';
import { historyService } from '../../services/HistoryService';
import api from '../../services/api';
import { useHelpContent } from '../../utils/HelpUtils';
import { normalizePresentation } from '../../utils/CategoryUtils';

function findCategoryInTree(categories: Category[], targetId: string): Category | null {
    const category = categories.find((cat) => cat.id === targetId);
    if (category) return category;
    for (const parent of categories) {
        if (parent.children) {
            const found = findCategoryInTree(parent.children, targetId);
            if (found) return found;
        }
    }
    return null;
}

/** Collapse nested category nodes so each top-level bucket has only content leaves (matches server organize apply). */
function collectSuggestedContentLeaves(node: any, out: any[]): void {
    for (const c of node.children || []) {
        if (c?.type === 'content') {
            out.push(c);
        } else if (c?.type === 'category') {
            collectSuggestedContentLeaves(c, out);
        }
    }
}

function flattenSuggestedCategoryChildrenToContentOnly(root: any): void {
    if (!root?.children) {
        return;
    }
    for (const child of root.children) {
        if (child?.type !== 'category') {
            continue;
        }
        const flat: any[] = [];
        collectSuggestedContentLeaves(child, flat);
        child.children = flat;
    }
}

type SuggestTreeOriginalContent = { id?: string; text: string; subcase?: string };

/** Matches Java {@code CategoryService.clusteringInputText} — keys align with embedding-service leaves. */
function clusteringInputText(text: string | null | undefined): string {
    if (text == null) return ' ';
    const n = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    if (n.trim() === '') return ' ';
    return n;
}

/** Map an AI content leaf to original row text + subcase for preview (leaves usually have text only, no index). */
function resolveOriginalContentForLeaf(
    leaf: { index?: number; text?: string; id?: string },
    originalContents: SuggestTreeOriginalContent[] | undefined,
    usedIndices: Set<number>,
): { text: string; subcase: string } | null {
    if (!originalContents?.length) {
        return leaf.text ? { text: leaf.text, subcase: '' } : null;
    }

    if (leaf.index !== undefined && leaf.index >= 0 && leaf.index < originalContents.length) {
        const o = originalContents[leaf.index];
        usedIndices.add(leaf.index);
        return { text: o.text ?? '', subcase: o.subcase ?? '' };
    }

    if (leaf.id) {
        const idx = originalContents.findIndex((o, i) => o.id === leaf.id && !usedIndices.has(i));
        if (idx >= 0) {
            usedIndices.add(idx);
            const o = originalContents[idx];
            return { text: o.text ?? '', subcase: o.subcase ?? '' };
        }
    }

    if (leaf.text) {
        const key = clusteringInputText(leaf.text);
        for (let i = 0; i < originalContents.length; i++) {
            if (usedIndices.has(i)) continue;
            if (clusteringInputText(originalContents[i].text) === key) {
                usedIndices.add(i);
                const o = originalContents[i];
                return { text: o.text ?? '', subcase: o.subcase ?? '' };
            }
        }
        const cleanText = leaf.text.replace(/^SUBCASE:\s*/i, '').trim();
        for (let i = 0; i < originalContents.length; i++) {
            if (usedIndices.has(i)) continue;
            const ot = originalContents[i].text ?? '';
            if (ot.includes(cleanText) || cleanText.includes(ot)) {
                usedIndices.add(i);
                const o = originalContents[i];
                return { text: o.text ?? '', subcase: o.subcase ?? '' };
            }
        }
        return { text: leaf.text, subcase: '' };
    }

    return null;
}

export type OrganizeCategoriesMode = 'fromContent' | 'branch';

export type OrganizeCategoriesPreset = 'initialCategories' | null;

type ChildSubtreeSuggestion = {
    categoryId: string;
    categoryName: string;
    suggested: { tree: any; originalContents?: SuggestTreeOriginalContent[] };
};

interface GenerateSubtreeDialogProps {
    open: boolean;
    onClose: () => void;
    mode: OrganizeCategoriesMode;
    /** When set on open, applies bottom-up strategy and depth 1 (initial categories workflow). */
    preset?: OrganizeCategoriesPreset;
    category: Category | null;
    anchorCategory?: Category | null;
    siblingCategoryIds?: string[];
    /** Direct children that are content categories with at least two rows (top-down on branch). */
    splittableChildCategories?: Category[];
    /** True while splittableChildCategories is still being resolved (branch mode). */
    splittableChildrenLoading?: boolean;
    categoriesTree?: Category[];
    onSuccess: (updatedCategoryId?: string, operationData?: any) => void;
}

const SOURCE_CAT_MARKER = /\n__SRC_CAT__=([^\n]+)$/;

const GenerateSubtreeDialog: React.FC<GenerateSubtreeDialogProps> = (props) => {
    const {
        open,
        onClose,
        mode,
        preset = null,
        category,
        anchorCategory = null,
        siblingCategoryIds = [],
        splittableChildCategories = [],
        splittableChildrenLoading = false,
        categoriesTree = [],
        onSuccess,
    } = props;
    const isFromContent = mode === 'fromContent';
    const isBranch = mode === 'branch';
    const anchor = isBranch ? anchorCategory : category;
    const canBranchBottomUp = siblingCategoryIds.length >= 2;
    const canBranchTopDown = splittableChildCategories.length >= 1;

    const [loading, setLoading] = useState(false);
    const [suggestedTree, setSuggestedTree] = useState<any>(null);
    const [childSuggestions, setChildSuggestions] = useState<ChildSubtreeSuggestion[] | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [showContent, setShowContent] = useState(true);
    const [maxDepth, setMaxDepth] = useState(3);
    const [strategy, setStrategy] = useState<'top_down' | 'bottom_up'>('top_down');
    const isBranchBottomUp = isBranch && strategy === 'bottom_up';
    const isBranchTopDown = isBranch && strategy === 'top_down';
    const [useSuggestedName, setUseSuggestedName] = useState(false);
    const [mergingOccurred, setMergingOccurred] = useState(false);
    const [context, setContext] = useState('');
    const [applyAiAnchorName, setApplyAiAnchorName] = useState(false);
    const generateAbortRef = useRef<AbortController | null>(null);
    const { getTooltipTextForState } = useHelpContent();
    const theme = useTheme();

    useEffect(() => {
        if (!open) return;
        setSuggestedTree(null);
        setChildSuggestions(null);
        setError(null);
        setMergingOccurred(false);
        setApplyAiAnchorName(false);
        setContext('');
        if (preset === 'initialCategories' && isFromContent) {
            setStrategy('bottom_up');
            setMaxDepth(1);
        } else if (isFromContent) {
            setStrategy('top_down');
            setMaxDepth(3);
        } else if (isBranch) {
            if (canBranchTopDown || splittableChildrenLoading) {
                setStrategy('top_down');
                setMaxDepth(3);
            } else if (canBranchBottomUp) {
                setStrategy('bottom_up');
                setMaxDepth(1);
            } else {
                setStrategy('top_down');
                setMaxDepth(3);
            }
        } else {
            setMaxDepth(1);
        }
    }, [open, preset, isFromContent, isBranch, canBranchBottomUp, canBranchTopDown, splittableChildrenLoading]);

    // Helper function to merge duplicate categories at the same level
    const mergeDuplicateCategories = (node: any): { node: any, merged: boolean } => {
        if (!node || !node.children || node.children.length === 0) {
            return { node, merged: false };
        }

        // Group children by name (for categories) or type
        const categoryGroups = new Map<string, any[]>();
        const contentNodes: any[] = [];
        let hasMerging = false;

        for (const child of node.children) {
            if (child.type === 'category') {
                const name = child.name || 'Unnamed Category';
                if (!categoryGroups.has(name)) {
                    categoryGroups.set(name, []);
                }
                categoryGroups.get(name)!.push(child);
            } else if (child.type === 'content') {
                contentNodes.push(child);
            }
        }

        // Merge categories with the same name
        const mergedCategories: any[] = [];
        for (const [name, categories] of Array.from(categoryGroups.entries())) {
            if (categories.length === 1) {
                // Single category, just merge its children
                const childResult = mergeDuplicateCategories(categories[0]);
                const mergedCategory = {
                    ...categories[0],
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            } else {
                // Multiple categories with the same name, merge them
                console.log(`Merging ${categories.length} categories with name: "${name}"`);
                hasMerging = true;
                
                // Collect all children from all categories with this name
                const allChildren: any[] = [];
                for (const category of categories) {
                    if (category.children && category.children.length > 0) {
                        allChildren.push(...category.children);
                    }
                }

                // Create a single merged category
                const childResult = mergeDuplicateCategories({ children: allChildren });
                const mergedCategory = {
                    name: name,
                    type: 'category',
                    children: childResult.node.children || []
                };
                mergedCategories.push(mergedCategory);
                if (childResult.merged) hasMerging = true;
            }
        }

        // Return the node with merged children
        return {
            node: {
                ...node,
                children: [...mergedCategories, ...contentNodes]
            },
            merged: hasMerging
        };
    };

    /** Server-side apply (one document lock; same createCategory/createContent as outline and move). */
    const applySuggestedTreeOnServer = async (
        anchorCategoryId: string,
        tree: any,
        withContent: boolean,
    ): Promise<{ tempStashCategoryId?: string }> => {
        const response = await api.post('/categories/apply-suggested-tree-from-content', {
            categoryId: anchorCategoryId,
            tree,
            withContent,
            strategy,
            maxDepth,
        });
        const tempStashCategoryId = response.data?.tempStashCategoryId as string | undefined;
        return { tempStashCategoryId };
    };

    const applyInitialCategoriesPreset = () => {
        setStrategy('bottom_up');
        setMaxDepth(1);
        setSuggestedTree(null);
        setChildSuggestions(null);
        setError(null);
    };

    const clearBranchPreview = () => {
        setSuggestedTree(null);
        setChildSuggestions(null);
        setMergingOccurred(false);
    };

    const handleGenerateFromContent = async () => {
        if (!category) return;
        try {
            setLoading(true);
            setError(null);
            const result = await suggestTreeFromCategory(category.id, maxDepth, context, strategy);
            const mergeResult = mergeDuplicateCategories(result.tree);
            if (strategy === 'bottom_up') {
                flattenSuggestedCategoryChildrenToContentOnly(mergeResult.node);
            }
            setMergingOccurred(mergeResult.merged);
            setSuggestedTree({ ...result, tree: mergeResult.node });
        } catch (err: any) {
            setError(err.message || 'Failed to generate category structure');
        } finally {
            setLoading(false);
        }
    };

    const handleRetryFromContent = async () => {
        if (!category) return;
        try {
            setLoading(true);
            setError(null);
            setMergingOccurred(false);
            const result = await suggestTreeFromCategory(category.id, maxDepth, context, strategy);
            const mergeResult = mergeDuplicateCategories(result.tree);
            if (strategy === 'bottom_up') {
                flattenSuggestedCategoryChildrenToContentOnly(mergeResult.node);
            }
            setMergingOccurred(mergeResult.merged);
            setSuggestedTree({ ...result, tree: mergeResult.node });
        } catch (err: any) {
            setError(err.message || 'Failed to regenerate category structure');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateSplitChildren = async () => {
        if (splittableChildCategories.length === 0) return;
        generateAbortRef.current?.abort();
        const abortController = new AbortController();
        generateAbortRef.current = abortController;
        const { signal } = abortController;
        setLoading(true);
        setError(null);
        setMergingOccurred(false);
        try {
            const results: ChildSubtreeSuggestion[] = [];
            let anyMerged = false;
            for (const child of splittableChildCategories) {
                if (signal.aborted) return;
                const result = await suggestTreeFromCategory(child.id, maxDepth, context, 'top_down');
                const mergeResult = mergeDuplicateCategories(result.tree);
                if (mergeResult.merged) anyMerged = true;
                results.push({
                    categoryId: child.id,
                    categoryName: child.name,
                    suggested: { ...result, tree: mergeResult.node },
                });
            }
            setChildSuggestions(results);
            setSuggestedTree(null);
            setMergingOccurred(anyMerged);
        } catch (err: any) {
            if (axios.isCancel(err)) return;
            setError(err.message || 'Failed to generate structure for subcategories');
            setChildSuggestions(null);
        } finally {
            if (generateAbortRef.current === abortController) {
                generateAbortRef.current = null;
            }
            setLoading(false);
        }
    };

    const handleGenerateGroupSiblings = async () => {
        if (!anchor || siblingCategoryIds.length < 2) return;
        generateAbortRef.current?.abort();
        const abortController = new AbortController();
        generateAbortRef.current = abortController;
        const { signal } = abortController;
        setLoading(true);
        setError(null);
        try {
            const data = await suggestParentStructureOverCategories(
                siblingCategoryIds,
                maxDepth,
                context.trim(),
                signal,
            );
            setSuggestedTree(data);
            setChildSuggestions(null);
        } catch (e: any) {
            if (axios.isCancel(e)) return;
            const timedOut =
                axios.isAxiosError(e) &&
                (e.code === 'ECONNABORTED' ||
                    String(e.message || '')
                        .toLowerCase()
                        .includes('timeout'));
            const minutes = Math.max(1, Math.round(SUGGEST_TREE_REQUEST_TIMEOUT_MS / 60_000));
            const msg = timedOut
                ? `The request timed out after about ${minutes} minutes. Try fewer siblings or a lower max height.`
                : axios.isAxiosError(e) && e.response?.data
                  ? typeof e.response.data === 'string'
                      ? e.response.data
                      : (e.response.data as any)?.message || e.message
                  : e instanceof Error
                    ? e.message
                    : 'Failed to generate structure';
            setError(String(msg));
            setSuggestedTree(null);
        } finally {
            if (generateAbortRef.current === abortController) {
                generateAbortRef.current = null;
            }
            setLoading(false);
        }
    };

    const historyOperationType =
        isBranchBottomUp
            ? 'proposeParentCategories'
            : preset === 'initialCategories' || (strategy === 'bottom_up' && maxDepth === 1)
              ? 'generateInitialCategories'
              : 'generateSubtree';

    const applySuggestionToContentCategory = async (
        cat: Category,
        suggestion: { tree: any; originalContents?: SuggestTreeOriginalContent[] },
        withContent: boolean,
        operationType: 'generateSubtree' | 'generateInitialCategories',
    ): Promise<string> => {
        const originalContents = cat.contents || [];
        await applySuggestedTreeOnServer(cat.id, suggestion.tree, withContent);
        const realGeneratedTree = await fetchCategoryWithChildren(cat.id);
        historyService.recordOperation({
            type: operationType,
            data: {
                originalCategory: cat,
                originalContents,
                tempCategory: undefined,
                generatedTree: realGeneratedTree,
            },
        });
        return cat.id;
    };

    const handleApplySplitChildrenWithContent = async () => {
        if (!childSuggestions?.length || !anchor) return;
        setLoading(true);
        setError(null);
        try {
            for (const item of childSuggestions) {
                const fresh = await fetchCategoryWithChildren(item.categoryId);
                if (!fresh?.contentCategory) continue;
                await applySuggestionToContentCategory(fresh, item.suggested, true, 'generateSubtree');
            }
            setChildSuggestions(null);
            onSuccess(anchor.id);
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to apply structure to subcategories');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptWithContent = async () => {
        if (!category || !suggestedTree) return;
        
        console.log('handleAcceptWithContent called with useSuggestedName:', useSuggestedName);
        
        setLoading(true);
        setError(null);
        
        try {
            const originalContents = category.contents || [];

            // Phase 1 (optional): Rename before apply
            let targetCategoryId = category.id;
            let renameSuccessful = false;
            
            if (useSuggestedName && suggestedTree.tree.name && suggestedTree.tree.name !== 'root') {
                console.log('Phase 2: Checking rename conditions...');
                
                // Check if we can rename this category (not a root category)
                if (category.parentId) {
                    console.log('✓ Category can be renamed - attempting rename');
                    console.log('Category details:', { 
                        categoryId: category.id, 
                        categoryName: category.name, 
                        categoryParentId: category.parentId,
                        newName: suggestedTree.tree.name
                    });
                    
                    try {
                        console.log('Making API call to rename category...');
                        const response = await api.put('/categories', {
                            parentId: category.id,
                            name: suggestedTree.tree.name,
                            description: category.description || ''
                        });
                        
                        console.log('✓ Successfully renamed category to:', suggestedTree.tree.name, 'Response:', response.data);
                        
                        // Use the updated category ID from the response
                        targetCategoryId = response.data.id;
                        renameSuccessful = true;
                        console.log('✓ Rename successful, new category ID:', targetCategoryId);
                    } catch (err: any) {
                        console.error('✗ Failed to rename category with suggested name:', err);
                        // Continue with original category ID since rename failed
                        targetCategoryId = category.id;
                        renameSuccessful = false;
                        console.log('Continuing with original category ID:', targetCategoryId);
                    }
                } else {
                    console.log('✗ Cannot rename root category, using original ID');
                    setUseSuggestedName(false);
                }
            } else {
                console.log('✗ Skipping rename - conditions not met');
            }

            await applySuggestedTreeOnServer(targetCategoryId, suggestedTree.tree, true);
            const realGeneratedTree = await fetchCategoryWithChildren(targetCategoryId);
            historyService.recordOperation({
                type: historyOperationType,
                data: {
                    originalCategory: category,
                    originalContents,
                    tempCategory: undefined,
                    generatedTree: realGeneratedTree,
                    renamedCategoryId: renameSuccessful ? targetCategoryId : undefined,
                    originalCategoryName: renameSuccessful ? category.name : undefined,
                },
            });
            onSuccess(renameSuccessful ? targetCategoryId : category.id);
            setSuggestedTree(null); // Clear the suggested tree
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to apply layout with content');
        } finally {
            setLoading(false);
        }
    };

    const handleAcceptStructureOnly = async () => {
        if (!suggestedTree || !category) return;
        
        setLoading(true);
        setError(null);
        
        try {
            let targetCategoryId = category.id;
            let renameSuccessful = false;
            let newCategoryId = category.id; // Store the new category ID separately

            // Rename before apply (server stashes rows into a temp bucket when structure-only)
            if (useSuggestedName && suggestedTree.tree.name && suggestedTree.tree.name !== 'root') {
                console.log('Phase 2: Checking rename conditions...');
                
                // Check if we can rename this category (not a root category)
                if (category.parentId) {
                    console.log('✓ Category can be renamed - attempting rename');
                    console.log('Category details:', { 
                        categoryId: category.id, 
                        categoryName: category.name, 
                        categoryParentId: category.parentId,
                        newName: suggestedTree.tree.name
                    });
                    
                    try {
                        console.log('Making API call to rename category...');
                        const response = await api.put('/categories', {
                            parentId: category.id,
                            name: suggestedTree.tree.name,
                            description: category.description || ''
                        });
                        
                        console.log('✓ Successfully renamed category to:', suggestedTree.tree.name, 'Response:', response.data);
                        
                        // Use the updated category ID from the response
                        newCategoryId = response.data.id; // Store in separate variable
                        targetCategoryId = newCategoryId; // Use for tree creation
                        renameSuccessful = true;
                        console.log('✓ Rename successful, new category ID:', newCategoryId);
                    } catch (err: any) {
                        console.error('✗ Failed to rename category with suggested name:', err);
                        // Continue with original category ID since rename failed
                        targetCategoryId = category.id;
                        newCategoryId = category.id;
                        renameSuccessful = false;
                        console.log('Continuing with original category ID:', targetCategoryId);
                    }
                } else {
                    console.log('✗ Cannot rename root category, using original ID');
                    setUseSuggestedName(false);
                }
            } else {
                console.log('✗ Skipping rename - conditions not met');
            }
            
            const { tempStashCategoryId } = await applySuggestedTreeOnServer(
                targetCategoryId,
                suggestedTree.tree,
                false,
            );

            const realGeneratedTree = await fetchCategoryWithChildren(targetCategoryId);

            const operationData = {
                originalCategory: category,
                originalContents: category.contents || [],
                tempCategory: tempStashCategoryId
                    ? { id: tempStashCategoryId, name: `Original Content - ${category.name}` } as Category
                    : undefined,
                generatedTree: realGeneratedTree,
                renamedCategoryId: renameSuccessful ? newCategoryId : undefined,
                originalCategoryName: renameSuccessful ? category.name : undefined
            };

            console.log('Recording generateSubtree operation for undo/redo:', operationData);
            historyService.recordOperation({
                type: historyOperationType,
                data: operationData
            });
            
            // Pass the updated category ID to the success callback
            // If rename was successful, use the new ID; otherwise use the original ID
            const finalCategoryId = renameSuccessful ? newCategoryId : category.id;
            onSuccess(finalCategoryId);
            setSuggestedTree(null); // Clear the suggested tree
            onClose();
        } catch (err: any) {
            setError(err.message || 'Failed to create subcategory structure');
        } finally {
            setLoading(false);
        }
    };

    const stripBomAndTrimCategoryId = (id: string) => id.replace(/^\uFEFF/g, '').trim();
    const proposeParentFormHeaders = { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } };
    const putCategoryMoveForm = (categoryId: string, newParentId: string) =>
        api.put(
            '/categories/move',
            new URLSearchParams({ categoryId, newParentId }).toString(),
            proposeParentFormHeaders,
        );
    const fetchCategoryById = (categoryId: string) => {
        const qs = new URLSearchParams();
        qs.set('categoryId', stripBomAndTrimCategoryId(categoryId));
        return api.get<Category>(`/categories?${qs.toString()}`);
    };

    const seedIdMapFromProposePreviewRoot = (previewRoot: { children?: any[] }) => {
        const map = new Map<string, string>();
        const walk = (node: any) => {
            if (!node || node.type !== 'category') return;
            const ch = node.children || [];
            if (ch.length === 1 && ch[0]?.type === 'content' && typeof ch[0]?.text === 'string') {
                const m = ch[0].text.match(SOURCE_CAT_MARKER);
                if (m?.[1]) map.set(stripBomAndTrimCategoryId(m[1]), stripBomAndTrimCategoryId(m[1]));
            }
            for (const sub of ch) {
                if (sub?.type === 'category') walk(sub);
            }
        };
        for (const sub of previewRoot?.children || []) {
            if (sub?.type === 'category') walk(sub);
        }
        return map;
    };

    const applySuggestParentNode = async (
        parentId: string,
        node: any,
        idMap: Map<string, string>,
        tracking: { createdCategoryIds: string[]; movesInApplyOrder: { originalId: string; previousParentId: string }[] },
    ): Promise<void> => {
        if (!node || node.type !== 'category') return;
        const ch = node.children || [];
        if (
            ch.length === 1 &&
            ch[0]?.type === 'content' &&
            typeof ch[0]?.text === 'string' &&
            ch[0].text.includes('__SRC_CAT__=')
        ) {
            const m = ch[0].text.match(SOURCE_CAT_MARKER);
            if (!m?.[1]) return;
            const origFromMarker = stripBomAndTrimCategoryId(m[1]);
            const catId = stripBomAndTrimCategoryId(idMap.get(origFromMarker) ?? origFromMarker);
            const moveTargetParentId = stripBomAndTrimCategoryId(parentId);
            let prevParentId = findCategoryInTree(categoriesTree, catId)?.parentId;
            if (typeof prevParentId !== 'string' || !prevParentId) {
                const catGet = await fetchCategoryById(catId);
                prevParentId = catGet.data?.parentId;
            }
            if (typeof prevParentId !== 'string' || !prevParentId) {
                throw new Error(`Could not resolve previous parent for category ${catId}`);
            }
            tracking.movesInApplyOrder.push({
                originalId: origFromMarker,
                previousParentId: stripBomAndTrimCategoryId(prevParentId),
            });
            const moveRes = await putCategoryMoveForm(catId, moveTargetParentId);
            const moved = moveRes.data as Category;
            if (moved?.id) idMap.set(origFromMarker, moved.id);
            return;
        }
        const res = await api.post('/categories', {
            parentId: stripBomAndTrimCategoryId(parentId),
            name: normalizePresentation((node.name || 'New category').trim()) || 'New category',
            description: '',
        });
        const newPid = res.data.id as string;
        tracking.createdCategoryIds.push(newPid);
        for (const sub of ch) {
            if (sub?.type === 'category') {
                await applySuggestParentNode(newPid, sub, idMap, tracking);
            }
        }
    };

    const handleApplyGroupSiblings = async () => {
        if (!anchor || !suggestedTree?.tree) return;
        try {
            setLoading(true);
            setError(null);
            const anchorLive = findCategoryInTree(categoriesTree, anchor.id);
            if (!anchorLive) {
                setError('The selected category is not in the current tree. Reload the document and try again.');
                return;
            }
            const anchorIdOriginal = anchorLive.id;
            const root = suggestedTree.tree;
            const tracking = { createdCategoryIds: [] as string[], movesInApplyOrder: [] as { originalId: string; previousParentId: string }[] };
            const idMap = seedIdMapFromProposePreviewRoot(root);
            let optionalAnchorRename:
                | { previousName: string; previousDescription: string; newName: string }
                | undefined;
            if (
                applyAiAnchorName &&
                anchorLive.parentId &&
                typeof root.name === 'string' &&
                root.name.trim() &&
                root.name.trim() !== anchorLive.name
            ) {
                optionalAnchorRename = {
                    previousName: anchorLive.name,
                    previousDescription: anchorLive.description || '',
                    newName: root.name.trim(),
                };
            }
            const groupedCategoryIds = Array.from(seedIdMapFromProposePreviewRoot(root).keys());
            const workAnchorId = stripBomAndTrimCategoryId(anchorIdOriginal);
            for (const sub of root.children || []) {
                if (sub?.type === 'category') {
                    await applySuggestParentNode(workAnchorId, sub, idMap, tracking);
                }
            }
            let anchorIdAfterApply = workAnchorId;
            if (optionalAnchorRename) {
                const anchorBeforeUmbrellaRename = anchorIdAfterApply;
                const ren = await api.put('/categories', {
                    parentId: anchorIdAfterApply,
                    name:
                        normalizePresentation(optionalAnchorRename.newName.trim()) ||
                        optionalAnchorRename.newName,
                    description: optionalAnchorRename.previousDescription,
                });
                anchorIdAfterApply = stripBomAndTrimCategoryId((ren.data as Category).id);
                const oldA = stripBomAndTrimCategoryId(anchorBeforeUmbrellaRename);
                const newA = stripBomAndTrimCategoryId(anchorIdAfterApply);
                if (oldA !== newA) {
                    const rebasePath = (p: string) => {
                        const v = stripBomAndTrimCategoryId(p);
                        if (v === oldA) return newA;
                        if (v.startsWith(`${oldA}:`)) return newA + v.slice(oldA.length);
                        return v;
                    };
                    for (const [k, v] of Array.from(idMap.entries())) {
                        idMap.set(k, rebasePath(v));
                    }
                    for (let i = 0; i < tracking.createdCategoryIds.length; i++) {
                        tracking.createdCategoryIds[i] = rebasePath(tracking.createdCategoryIds[i]);
                    }
                }
            }
            historyService.recordOperation({
                type: 'proposeParentCategories',
                data: {
                    anchorCategoryId: anchorIdAfterApply,
                    anchorCategoryIdBeforeApply: anchorIdOriginal,
                    previewTreeRoot: root,
                    groupedCategoryIds,
                    createdCategoryIds: tracking.createdCategoryIds,
                    movesInApplyOrder: tracking.movesInApplyOrder,
                    finalIdsByOriginalId: Object.fromEntries(idMap),
                    optionalAnchorRename,
                },
            });
            setSuggestedTree(null);
            onSuccess(anchorIdAfterApply);
            onClose();
        } catch (err: any) {
            setError(err instanceof Error ? err.message : 'Failed to apply suggested structure');
        } finally {
            setLoading(false);
        }
    };

    const renderGroupSiblingsPreview = (node: any, depth = 0, isPreviewRoot = false): React.ReactNode => {
        if (!node || node.type !== 'category') return null;
        const displayName = isPreviewRoot && anchor ? anchor.name : node.name;
        const aiTheme =
            isPreviewRoot && anchor && typeof node.name === 'string' && node.name !== anchor.name
                ? node.name
                : null;
        return (
            <Box key={`${depth}-${displayName}`} sx={{ pl: depth * 2 }}>
                <Typography variant="body2">📁 {displayName}</Typography>
                {aiTheme && (
                    <Typography variant="caption" color="text.secondary" display="block">
                        Suggested umbrella title: {aiTheme}
                    </Typography>
                )}
                {(node.children || []).map((sub: any, i: number) =>
                    sub?.type === 'category' ? renderGroupSiblingsPreview(sub, depth + 1, false) : null,
                )}
            </Box>
        );
    };

    const handleClose = () => {
        generateAbortRef.current?.abort();
        generateAbortRef.current = null;
        setSuggestedTree(null);
        setChildSuggestions(null);
        setError(null);
        setShowContent(true);
        setMaxDepth(3);
        setStrategy('top_down');
        setUseSuggestedName(false);
        setMergingOccurred(false);
        setContext('');
        setApplyAiAnchorName(false);
        onClose();
    };

    // Helper to detect RTL (Hebrew/Arabic)
    const isRTL = suggestedTree && suggestedTree.tree && /[\u0590-\u05FF\u0600-\u06FF]/.test(suggestedTree.tree.name);

    // Helper function to detect content categories
    const isContentCategory = (node: any): boolean => {
        if (!node.children || node.children.length === 0) {
            return true; // No children = content category
        }
        // Has children of type 'content' = content category
        return node.children.some((child: any) => child.type === 'content');
    };

    // Helper to render tree preview
    const renderTree = (
        node: any,
        path: string = '',
        level: number = 0,
        usedIndices: Set<number> = new Set(),
        originalContentsOverride?: SuggestTreeOriginalContent[],
    ): React.ReactNode[] => {
        const originalContentsForLeaves = originalContentsOverride ?? suggestedTree?.originalContents;
        if (!node) return [];
        const currentPath = path + (node.name || node.text || '');
        const padding = isRTL ? { pr: level * 2 } : { pl: level * 2 };

        // Category node (including root)
        if (node.type === 'category' && node.name) {
            const children = Array.isArray(node.children)
                ? node.children.flatMap((child: any, idx: number) =>
                    renderTree(child, currentPath + '-' + idx, level + 1, usedIndices, originalContentsForLeaves))
                : [];
            const isContentCat = isContentCategory(node);
            const iconColor = isContentCat ? theme.palette.info.main : theme.palette.primary.main;
            return [
                <Box key={currentPath} sx={{ display: 'flex', alignItems: 'center', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                    {isContentCat ? (
                        <DescriptionIcon sx={{ color: iconColor, mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    ) : (
                    <FolderIcon sx={{ color: iconColor, mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
                    )}
                    <Typography variant="body1" sx={{ fontWeight: 700, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                        {node.name}
                    </Typography>
                </Box>,
                ...children
            ];
        }

        // Content node
        if (showContent && node.type === 'content') {
            const resolved = resolveOriginalContentForLeaf(
                node,
                originalContentsForLeaves,
                usedIndices,
            );
            const displayText = resolved?.text ?? '';
            const subcaseText = resolved?.subcase ?? '';

            if (displayText || subcaseText) {
                return [
                    <Box key={currentPath} sx={{ display: 'flex', flexDirection: 'column', ...padding, mb: 0.5 }} dir={isRTL ? 'rtl' : 'ltr'}>
                        {subcaseText && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                                <StickyNote2OutlinedIcon sx={{ color: 'warning.main', mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0, fontSize: 18, mt: 0.5 }} />
                                <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600, wordBreak: 'break-word', textAlign: isRTL ? 'right' : 'left' }} dir="auto">
                                    {subcaseText}
                                </Typography>
                            </Box>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', wordBreak: 'break-word', whiteSpace: 'pre-wrap', lineHeight: 1.5, textAlign: isRTL ? 'right' : 'left', flex: 1 }} dir="auto">
                                {displayText}
                            </Typography>
                        </Box>
                    </Box>
                ];
            }
        }

        return [];
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                Structure categories (AI)
                {anchor && (
                    <Typography variant="subtitle2" color="text.secondary">
                        {isBranch ? 'Branch' : 'Content category'}: {anchor.name}
                    </Typography>
                )}
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontWeight: 400 }}>
                    {isBranch
                        ? 'Top-down splits each content subcategory into a folder tree; bottom-up groups siblings under new parents.'
                        : 'Cluster rows into folders; choose depth and top-down or bottom-up.'}
                </Typography>
            </DialogTitle>
            
            <DialogContent sx={{ position: 'relative' }}>
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: (t) => alpha(t.palette.background.paper, 0.92),
                            zIndex: 10,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                {!suggestedTree && !childSuggestions && (
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            {isBranch
                                ? isBranchBottomUp
                                  ? 'Bottom-up: AI adds parent folders above the subcategories you checked in the content pane (at least three). Leave all unchecked to include every direct child. Max height controls new parent layers.'
                                  : 'Top-down: AI builds a folder tree inside each checked content subcategory with at least two rows (or every eligible child if none are checked). Branch subcategories are skipped.'
                                : strategy === 'bottom_up'
                                  ? 'Bottom-up: finer groupings first — more categories with fewer rows in each. Top-down: broader folders first — fewer categories with more rows in each.'
                                  : 'Top-down: broader folders first — fewer categories with more rows in each. Bottom-up: finer groupings first — more categories with fewer rows in each.'}
                        </Typography>

                        {(isFromContent || isBranch) && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                <ToggleButtonGroup
                                    exclusive
                                    size="small"
                                    value={strategy}
                                    onChange={(_, v) => {
                                        if (!v) return;
                                        setStrategy(v);
                                        clearBranchPreview();
                                    }}
                                >
                                    <ToggleButton
                                        value="top_down"
                                        disabled={isBranch && !canBranchTopDown && !splittableChildrenLoading}
                                    >
                                        Top-down
                                    </ToggleButton>
                                    <ToggleButton value="bottom_up" disabled={isBranch && !canBranchBottomUp}>
                                        Bottom-up
                                    </ToggleButton>
                                </ToggleButtonGroup>
                                {isBranch && splittableChildrenLoading && (
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 0.75 }}
                                    >
                                        <CircularProgress size={12} />
                                        Checking subcategories…
                                    </Typography>
                                )}
                                {isBranch && !splittableChildrenLoading && !canBranchTopDown && canBranchBottomUp && (
                                    <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
                                        Top-down needs content subcategories (document icon) with at least two rows in your selection.
                                    </Typography>
                                )}
                                {isFromContent && (
                                    <Button size="small" variant="outlined" onClick={applyInitialCategoriesPreset}>
                                        Initial categories (depth 1, bottom-up)
                                    </Button>
                                )}
                            </Box>
                        )}

                        <Tooltip title={getTooltipTextForState('suggest-tree-context', true)} placement="top">
                            <TextField
                                fullWidth
                                label="Context (Optional)"
                                value={context}
                                onChange={(e) => setContext(e.target.value)}
                                placeholder="Domain, purpose, or naming hints for the AI…"
                                multiline
                                rows={3}
                                margin="normal"
                            />
                        </Tooltip>

                        <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>
                            {isBranchBottomUp ? 'Max height (new parent levels)' : 'Maximum depth'}: {maxDepth}
                        </Typography>
                        <Slider
                            value={maxDepth}
                            onChange={(_, value) => setMaxDepth(value as number)}
                            min={1}
                            max={5}
                            marks
                            valueLabelDisplay="auto"
                            sx={{ mb: 2 }}
                        />

                        <Button
                            variant="contained"
                            onClick={
                                isBranchBottomUp
                                    ? handleGenerateGroupSiblings
                                    : isBranchTopDown
                                      ? handleGenerateSplitChildren
                                      : handleGenerateFromContent
                            }
                            disabled={
                                loading ||
                                (isFromContent && !category) ||
                                (isBranchBottomUp && (!anchor || siblingCategoryIds.length < 2)) ||
                                (isBranchTopDown && splittableChildrenLoading) ||
                                (isBranchTopDown && !splittableChildrenLoading && splittableChildCategories.length === 0)
                            }
                        >
                            Generate preview
                        </Button>
                    </Box>
                )}

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {mergingOccurred && (isFromContent || isBranchTopDown) && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Some categories with duplicate names were automatically merged to maintain a valid structure.
                    </Alert>
                )}

                {suggestedTree && isFromContent && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Suggested Structure
                        </Typography>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <ToggleButtonGroup
                                exclusive
                                size="small"
                                value={strategy}
                                onChange={(_, v) => v && setStrategy(v)}
                                disabled={loading}
                            >
                                <ToggleButton value="top_down">Top-down</ToggleButton>
                                <ToggleButton value="bottom_up">Bottom-up</ToggleButton>
                            </ToggleButtonGroup>
                            <Box sx={{ minWidth: 160, flex: 1 }}>
                                <Typography variant="body2" gutterBottom>
                                    Depth: {maxDepth}
                                </Typography>
                                <Slider
                                    value={maxDepth}
                                    onChange={(_, value) => setMaxDepth(value as number)}
                                    min={1}
                                    max={5}
                                    marks
                                    valueLabelDisplay="auto"
                                    disabled={loading}
                                />
                            </Box>
                            <Button variant="contained" onClick={handleRetryFromContent} disabled={loading}>
                                Retry
                            </Button>
                        </Box>
                        <FormControlLabel
                            control={
                                <Checkbox checked={showContent} onChange={(e) => setShowContent(e.target.checked)} />
                            }
                            label="Show content preview"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={useSuggestedName}
                                    onChange={(e) => setUseSuggestedName(e.target.checked)}
                                    disabled={!category?.parentId}
                                />
                            }
                            label={
                                !category?.parentId
                                    ? `Cannot rename root category: "${suggestedTree.tree.name}"`
                                    : `Use AI-suggested name: "${suggestedTree.tree.name}"`
                            }
                        />
                        <Box
                            sx={{
                                maxHeight: 400,
                                overflow: 'auto',
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                mt: 2,
                                bgcolor: (t) =>
                                    t.palette.mode === 'dark'
                                        ? alpha(t.palette.common.white, 0.04)
                                        : alpha(t.palette.common.black, 0.02),
                            }}
                        >
                            {renderTree(suggestedTree.tree, '', 0, new Set())}
                        </Box>
                    </Box>
                )}

                {childSuggestions && isBranchTopDown && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Suggested structure per subcategory
                        </Typography>
                        <FormControlLabel
                            control={
                                <Checkbox checked={showContent} onChange={(e) => setShowContent(e.target.checked)} />
                            }
                            label="Show content preview"
                        />
                        {childSuggestions.map((item) => (
                            <Box key={item.categoryId} sx={{ mb: 2 }}>
                                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                                    {item.categoryName}
                                </Typography>
                                <Box
                                    sx={{
                                        maxHeight: 280,
                                        overflow: 'auto',
                                        border: 1,
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        p: 2,
                                        bgcolor: (t) =>
                                            t.palette.mode === 'dark'
                                                ? alpha(t.palette.common.white, 0.04)
                                                : alpha(t.palette.common.black, 0.02),
                                    }}
                                >
                                    {renderTree(item.suggested.tree, '', 0, new Set(), item.suggested.originalContents)}
                                </Box>
                            </Box>
                        ))}
                        <Button
                            variant="outlined"
                            onClick={handleGenerateSplitChildren}
                            disabled={loading}
                            sx={{ mt: 1 }}
                        >
                            Retry
                        </Button>
                    </Box>
                )}

                {suggestedTree && isBranchBottomUp && (
                    <Box>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Suggested parent layers
                        </Typography>
                        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 160, flex: 1 }}>
                                <Typography variant="body2" gutterBottom>
                                    Max height: {maxDepth}
                                </Typography>
                                <Slider
                                    value={maxDepth}
                                    onChange={(_, value) => setMaxDepth(value as number)}
                                    min={1}
                                    max={5}
                                    marks
                                    valueLabelDisplay="auto"
                                    disabled={loading}
                                />
                            </Box>
                            <Button variant="outlined" onClick={handleGenerateGroupSiblings} disabled={loading}>
                                Retry
                            </Button>
                        </Box>
                        {anchor?.parentId &&
                            typeof suggestedTree.tree.name === 'string' &&
                            suggestedTree.tree.name.trim() &&
                            suggestedTree.tree.name.trim() !== anchor.name && (
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={applyAiAnchorName}
                                            onChange={(e) => setApplyAiAnchorName(e.target.checked)}
                                        />
                                    }
                                    label={`Also rename “${anchor.name}” to “${suggestedTree.tree.name.trim()}”`}
                                    sx={{ mb: 1 }}
                                />
                            )}
                        <Box
                            sx={{
                                border: 1,
                                borderColor: 'divider',
                                borderRadius: 1,
                                p: 2,
                                maxHeight: 400,
                                overflow: 'auto',
                                bgcolor: (t) =>
                                    t.palette.mode === 'dark'
                                        ? alpha(t.palette.common.white, 0.04)
                                        : alpha(t.palette.common.black, 0.02),
                            }}
                        >
                            {renderGroupSiblingsPreview(suggestedTree.tree, 0, true)}
                        </Box>
                    </Box>
                )}
            </DialogContent>
            
            <DialogActions>
                {suggestedTree && isFromContent && (
                    <>
                        <Button variant="contained" onClick={handleAcceptWithContent} disabled={loading}>
                            Generate with Content
                        </Button>
                        <Button variant="contained" onClick={handleAcceptStructureOnly} disabled={loading}>
                            Generate Structure Only
                        </Button>
                    </>
                )}
                {suggestedTree && isBranchBottomUp && (
                    <Button variant="contained" onClick={handleApplyGroupSiblings} disabled={loading}>
                        Apply
                    </Button>
                )}
                {childSuggestions && isBranchTopDown && (
                    <Button variant="contained" onClick={handleApplySplitChildrenWithContent} disabled={loading}>
                        Apply to all
                    </Button>
                )}
                <Button onClick={handleClose}>Cancel</Button>
            </DialogActions>
        </Dialog>
    );
};

export default GenerateSubtreeDialog; 